import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getSetpointDiligence,
  getSetpointFacilities,
  getSetpointRecord,
  pushSetpoint,
  saveSetpointDocuments,
  sendSetpointDocuments,
} from 'services'
import Api from 'services/api'
import { Alert, Button, DocumentFile, InputFileTable, PlainTable, Select2 } from 'stories/components'
import { renderSortButton } from 'utils'

import type {
  DiligencePackage,
  DiligenceRequirement,
  SetpointFacility,
  SetpointRecord,
  SetpointStoredDocument,
} from './types'

type RequirementSortKey = 'name' | 'stage' | 'level'

function flattenRequirements(requirements: DiligenceRequirement[]): DiligenceRequirement[] {
  const out: DiligenceRequirement[] = []
  const walk = (items: DiligenceRequirement[]) => {
    ;(items || []).forEach((item) => {
      if (!item) return
      out.push(item)
      if (Array.isArray(item.tasks) && item.tasks.length) walk(item.tasks)
    })
  }
  walk(requirements || [])
  return out
}

function buildRequirementLabels(requirements: DiligenceRequirement[]) {
  const flat = flattenRequirements(requirements)
  // Prefer document requirements; fall back to any requirement with an id so Category is usable.
  const withId = flat.filter((r) => !!r.globalId)
  let docReqs = withId.filter((r) => r.isDocument === true)
  if (docReqs.length === 0) docReqs = withId

  const nameCounts: Record<string, number> = {}
  docReqs.forEach((r) => {
    const name = (r.name || 'Requirement').trim() || 'Requirement'
    nameCounts[name] = (nameCounts[name] || 0) + 1
  })

  const labelToId: Record<string, string> = {}
  const idToLabel: Record<string, string> = {}
  const labels: string[] = []

  docReqs.forEach((r) => {
    const base = (r.name || 'Requirement').trim() || 'Requirement'
    const label =
      nameCounts[base] > 1 ? `${base} [${String(r.globalId).slice(0, 8)}]` : base
    labelToId[label] = String(r.globalId)
    idToLabel[String(r.globalId)] = label
    labels.push(label)
  })

  return { labels, labelToId, idToLabel }
}

function normalizeStatus(status?: string | null): 'pending' | 'sent' | 'failed' {
  if (status === 'sent' || status === 'failed') return status
  return 'pending'
}

function toDocumentFiles(
  docs: SetpointStoredDocument[],
  idToLabel: Record<string, string>,
): DocumentFile[] {
  return (docs || []).map((doc) => ({
    id: doc.id,
    name: doc.name,
    fileKey: doc.fileKey,
    status: normalizeStatus(doc.status),
    category: (doc.requirementId && idToLabel[doc.requirementId]) || doc.requirementName || '',
    requirementId: doc.requirementId || '',
    requirementName: doc.requirementName || '',
    error: doc.error || null,
    comment: doc.comment || null,
    createdAt: doc.createdAt || Date.now(),
    sentAt: doc.sentAt || null,
  }))
}

function toStoredDocuments(
  files: DocumentFile[],
  labelToId: Record<string, string>,
): SetpointStoredDocument[] {
  return files.map((file) => {
    const category = String(file.category || '').trim()
    const requirementId = labelToId[category] || String(file.requirementId || '')
    const requirementName = category || String(file.requirementName || '')
    return {
      id: file.id,
      name: file.name,
      fileKey: file.fileKey,
      requirementId,
      requirementName,
      status: normalizeStatus(file.status),
      error: file.error ?? null,
      comment: file.comment ?? null,
      createdAt: file.createdAt,
      sentAt: file.sentAt ?? null,
    }
  })
}

export default function SetPoint() {
  const [loading, setLoading] = useState(false)
  const [pushResult, setPushResult] = useState<Record<string, any> | null>(null)
  const [diligence, setDiligence] = useState<DiligencePackage | null>(null)
  const [facilities, setFacilities] = useState<SetpointFacility[]>([])
  const [facilityGlobalId, setFacilityGlobalId] = useState('')
  const [facilityLocked, setFacilityLocked] = useState(false)
  const [customerAssetId, setCustomerAssetId] = useState('')
  const [documents, setDocuments] = useState<DocumentFile[]>([])
  const [error, setError] = useState('')
  const [reqSort, setReqSort] = useState<{ key: RequirementSortKey; dir: 1 | -1 }>({
    key: 'name',
    dir: 1,
  })
  const loanNumber = Api.getLoanNumber()

  const facilityOptions = useMemo(() => {
    const options: Record<string, string> = {}
    facilities.forEach((f) => {
      options[f.globalId] = f.name || f.globalId
    })
    return options
  }, [facilities])

  const requirementMeta = useMemo(
    () => buildRequirementLabels(diligence?.requirements || []),
    [diligence?.requirements],
  )

  const hasAsset = !!customerAssetId || !!diligence
  const pendingCount = documents.filter(
    (d) => (d.status === 'pending' || d.status === 'failed') && (d.category || d.requirementId),
  ).length

  const loadPage = async (silent = true) => {
    setLoading(true)
    setError('')
    try {
      const [facilitiesRes, diligenceRes, recordRes] = await Promise.all([
        getSetpointFacilities(),
        getSetpointDiligence(),
        getSetpointRecord(),
      ])
      const nextFacilities: SetpointFacility[] = facilitiesRes?.facilities || []
      setFacilities(nextFacilities)

      const nextDiligence = diligenceRes?.diligence || null
      setDiligence(nextDiligence)

      const record: SetpointRecord = recordRes?.record || {}
      const nextAssetId =
        diligenceRes?.customerAssetId || record.customerAssetId || nextDiligence?.customerAssetId || ''
      setCustomerAssetId(nextAssetId || '')

      const lockedFacility =
        diligenceRes?.facilityGlobalId ||
        nextDiligence?.facilityGlobalId ||
        record.facilityGlobalId ||
        ''
      if (nextDiligence && lockedFacility) {
        setFacilityGlobalId(lockedFacility)
        setFacilityLocked(true)
      } else if (record.facilityGlobalId) {
        setFacilityGlobalId(record.facilityGlobalId)
        setFacilityLocked(!!nextDiligence)
      } else {
        setFacilityLocked(false)
        setFacilityGlobalId((prev) => prev || '')
      }

      const meta = buildRequirementLabels(nextDiligence?.requirements || [])
      const storedDocs: SetpointStoredDocument[] =
        record.documents || diligenceRes?.documents || []
      setDocuments(toDocumentFiles(storedDocs, meta.idToLabel))

      if (!silent) {
        if (nextDiligence) toast('Diligence loaded', { type: 'success' })
        else toast('Loan not found in Setpoint yet. Push to create it.', { type: 'info' })
      }
    } catch (e: any) {
      setDiligence(null)
      setFacilityLocked(false)
      const message = e?.response?.data?.message || e?.message || 'Failed to load Setpoint data'
      setError(Array.isArray(message) ? message.join(', ') : message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPage(true)
  }, [])

  const onPush = async () => {
    if (!facilityGlobalId) {
      toast('Select a facility before pushing', { type: 'error' })
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await pushSetpoint(facilityGlobalId)
      setPushResult(res)
      if (res?.customerAssetId) setCustomerAssetId(res.customerAssetId)
      toast('Loan pushed to Setpoint', { type: 'success' })
      await loadPage(true)
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || 'Failed to push loan to Setpoint'
      setError(Array.isArray(message) ? message.join(', ') : message)
      toast(Array.isArray(message) ? message.join(', ') : message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onChangeDocuments = async (files: DocumentFile[]) => {
    const mapped = toStoredDocuments(files, requirementMeta.labelToId)
    const uiFiles = toDocumentFiles(mapped, requirementMeta.idToLabel)
    setDocuments(uiFiles)
    try {
      const res = await saveSetpointDocuments(mapped)
      const saved: SetpointStoredDocument[] = res?.record?.documents || mapped
      setDocuments(toDocumentFiles(saved, requirementMeta.idToLabel))
      if (res?.record?.customerAssetId) setCustomerAssetId(res.record.customerAssetId)
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || 'Failed to save Setpoint documents'
      toast(Array.isArray(message) ? message.join(', ') : message, { type: 'error' })
    }
  }

  const onSendDocuments = async () => {
    const missingCategory = documents.some(
      (d) => (d.status === 'pending' || d.status === 'failed') && !d.category && !d.requirementId,
    )
    if (missingCategory) {
      toast('Assign a funding requirement (Category) to each document before sending', {
        type: 'error',
      })
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await sendSetpointDocuments()
      const saved: SetpointStoredDocument[] = res?.record?.documents || []
      setDocuments(toDocumentFiles(saved, requirementMeta.idToLabel))
      if (res?.customerAssetId) setCustomerAssetId(res.customerAssetId)

      const failed = (res?.results || []).filter((r: any) => r.status === 'failed')
      const sent = (res?.results || []).filter((r: any) => r.status === 'sent')
      if (failed.length) {
        toast(`${failed.length} document(s) failed to send`, { type: 'error' })
      } else {
        toast(`${sent.length} document(s) sent to Setpoint`, { type: 'success' })
      }
      await loadPage(true)
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || 'Failed to send documents to Setpoint'
      setError(Array.isArray(message) ? message.join(', ') : message)
      toast(Array.isArray(message) ? message.join(', ') : message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const exceptionCounts = {
    data: diligence?.exceptions?.dataExceptions?.length || 0,
    document: diligence?.exceptions?.documentExceptions?.length || 0,
    guideline: diligence?.exceptions?.guidelineExceptions?.length || 0,
  }

  const gradesData = useMemo(
    () => (diligence?.grades || []).map((g) => [g.displayName || '—', g.grade || '—', g.updatedAt || '—']),
    [diligence?.grades],
  )

  const guidelinesData = useMemo(
    () =>
      (diligence?.guidelines || []).map((g) => [g.displayName || '—', g.status || '—', g.explanation || '—']),
    [diligence?.guidelines],
  )

  const requirementsData = useMemo(() => {
    const flat = flattenRequirements(diligence?.requirements || [])
    const groups = new Map<
      string,
      {
        name: string
        stage: string
        level: string
        variants: Array<{ isWaived: boolean; isDocument: boolean }>
      }
    >()

    flat.forEach((r) => {
      const name = r.name || '—'
      const stage = r.stage || '—'
      const level = r.level || '—'
      const key = `${name}\0${stage}\0${level}`
      const existing = groups.get(key)
      const variant = { isWaived: !!r.isWaived, isDocument: !!r.isDocument }
      if (existing) {
        const already = existing.variants.some(
          (v) => v.isWaived === variant.isWaived && v.isDocument === variant.isDocument,
        )
        if (!already) existing.variants.push(variant)
      } else {
        groups.set(key, { name, stage, level, variants: [variant] })
      }
    })

    const localeCmp = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase())

    return Array.from(groups.values())
      .sort((a, b) => {
        const primary = localeCmp(a[reqSort.key], b[reqSort.key]) * reqSort.dir
        if (primary !== 0) return primary
        const byName = localeCmp(a.name, b.name)
        if (byName !== 0 && reqSort.key !== 'name') return byName
        const byStage = localeCmp(a.stage, b.stage)
        if (byStage !== 0 && reqSort.key !== 'stage') return byStage
        if (reqSort.key !== 'level') return localeCmp(a.level, b.level)
        return 0
      })
      .map((group, index) => {
        const waivedDocumentCell =
          group.variants.length === 1 ? (
            <>
              {group.variants[0].isWaived ? 'Y' : 'N'} / {group.variants[0].isDocument ? 'Y' : 'N'}
            </>
          ) : (
            <table className="text-xs border border-gray-200 rounded overflow-hidden">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-2 py-1 font-medium">Waived</th>
                  <th className="px-2 py-1 font-medium">Document</th>
                </tr>
              </thead>
              <tbody>
                {group.variants.map((v, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-2 py-1">{v.isWaived ? 'Y' : 'N'}</td>
                    <td className="px-2 py-1">{v.isDocument ? 'Y' : 'N'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )

        return [index + 1, group.name, group.stage, group.level, waivedDocumentCell]
      })
  }, [diligence?.requirements, reqSort])

  const onRequirementSort = (key: RequirementSortKey) => {
    setReqSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 },
    )
  }

  const requirementsHeader = useMemo(
    () => [
      'No',
      renderSortButton({
        title: 'Name',
        sortOrder: reqSort.key === 'name' ? reqSort.dir : 0,
        onClick: () => onRequirementSort('name'),
      }),
      renderSortButton({
        title: 'Stage',
        sortOrder: reqSort.key === 'stage' ? reqSort.dir : 0,
        onClick: () => onRequirementSort('stage'),
      }),
      renderSortButton({
        title: 'Level',
        sortOrder: reqSort.key === 'level' ? reqSort.dir : 0,
        onClick: () => onRequirementSort('level'),
      }),
      'Waived / Document',
    ],
    [reqSort],
  )

  const documentStatusData = useMemo(
    () =>
      documents.map((d) => [
        d.name || '—',
        d.category || d.requirementName || '—',
        d.status || 'pending',
        d.error || '—',
      ]),
    [documents],
  )

  return (
    <div className="relative min-h-[24rem]">
      <LayoutLoading show={loading} />
      <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
        <span>SetPoint</span>
      </h2>

      <div className="max-w-md mb-4">
        <Select2
          id="setpointFacility"
          title="Facility"
          options={facilityOptions}
          value={facilityGlobalId}
          hasDefaultOption={true}
          disabled={facilityLocked}
          onChange={(value: string) => {
            if (!facilityLocked) setFacilityGlobalId(value)
          }}
        />
        {facilityLocked ? (
          <p className="text-sm text-gray-500 mt-1">Facility is locked after the loan is pushed to Setpoint.</p>
        ) : (
          <p className="text-sm text-gray-500 mt-1">Select a facility before pushing this loan.</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button onClick={onPush} loading={loading} disabled={!facilityGlobalId}>
          Push to Setpoint
        </Button>
        <Button outline color="gray" onClick={() => loadPage(false)} loading={loading}>
          Refresh Diligence
        </Button>
      </div>

      {!!error && (
        <div className="mb-4 max-w-3xl">
          <Alert color="red">{error}</Alert>
        </div>
      )}

      {!!pushResult && (
        <div className="mb-6 p-4 border rounded bg-zinc-50">
          <h3 className="font-semibold mb-2">Last Push Result</h3>
          <p className="text-sm text-gray-600 mb-1">Customer Asset ID: {pushResult.customerAssetId}</p>
          <p className="text-sm text-gray-600 mb-1">Facility: {pushResult.facilityGlobalId || '—'}</p>
          <p className="text-sm text-gray-600 mb-1">
            Created: {(pushResult.createdIds || []).join(', ') || '—'}
          </p>
          <p className="text-sm text-gray-600">Updated: {(pushResult.updatedIds || []).join(', ') || '—'}</p>
        </div>
      )}

      {!diligence && !error && (
        <Alert color="yellow">No diligence data yet. Push the loan to Setpoint, then refresh.</Alert>
      )}

      {!!diligence && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-1">Asset Overview</h3>
            <p className="text-sm text-gray-600">Asset ID: {diligence.customerAssetId || customerAssetId || '—'}</p>
            <p className="text-sm text-gray-600">Display Name: {diligence.displayName || '—'}</p>
            <p className="text-sm text-gray-600">Certification: {diligence.certificationDate || '—'}</p>
            {!!diligence.diligenceComments && (
              <p className="text-sm text-gray-600 mt-1">Comments: {diligence.diligenceComments}</p>
            )}
            <p className="text-sm text-gray-600 mt-1">
              Exceptions — Data: {exceptionCounts.data}, Document: {exceptionCounts.document}, Guideline:{' '}
              {exceptionCounts.guideline}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Grades</h3>
            {gradesData.length === 0 ? (
              <p className="text-sm text-gray-500">No grades</p>
            ) : (
              <PlainTable header={['Name', 'Grade', 'Updated']} data={gradesData} />
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Guidelines</h3>
            {guidelinesData.length === 0 ? (
              <p className="text-sm text-gray-500">No guidelines</p>
            ) : (
              <PlainTable header={['Name', 'Status', 'Explanation']} data={guidelinesData} />
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Funding Requirements</h3>
            {requirementsData.length === 0 ? (
              <p className="text-sm text-gray-500">No requirements</p>
            ) : (
              <PlainTable header={requirementsHeader} data={requirementsData} />
            )}
          </div>
        </div>
      )}

      <div className="mt-8 space-y-3">
        <h3 className="text-lg font-semibold">Documents</h3>
        <p className="text-sm text-gray-500">
          Upload files or drag from the documents sidebar. Set Category to the Setpoint funding requirement,
          then send.
        </p>
        {!hasAsset && (
          <Alert color="yellow">Push the loan to Setpoint before uploading documents.</Alert>
        )}
        {hasAsset && requirementMeta.labels.length === 0 && (
          <Alert color="yellow">
            No document funding requirements returned yet. Refresh diligence after push.
          </Alert>
        )}
        <InputFileTable
          title="SetPoint Documents"
          filePath={`loan/${loanNumber}/setpoint`}
          name="setpointDocuments"
          value={documents}
          onChange={onChangeDocuments}
          showCategory={true}
          canEditCategory={true}
          categories={requirementMeta.labels.length ? requirementMeta.labels : ['Unassigned']}
          showStatus={false}
          multiple={true}
          uploadable={hasAsset}
          disabled={!hasAsset}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={onSendDocuments}
            loading={loading}
            disabled={!hasAsset || pendingCount === 0}
          >
            Send to Setpoint
          </Button>
          <span className="text-sm text-gray-500">
            {pendingCount} pending/failed document(s) ready to send
          </span>
        </div>
        {documentStatusData.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2 text-sm">Send Status</h4>
            <PlainTable header={['File', 'Requirement', 'Status', 'Error']} data={documentStatusData} />
          </div>
        )}
      </div>
    </div>
  )
}
